import { type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { SPINOFF_AUTH_CONFIG } from '../auth/spinoff-auth.config';
import { SpinoffTwoFactorAuthService } from './spinoff-two-factor-auth.service';
import {
  SUPPORTED_SPINOFF_TWO_FACTOR_AUTH_METHODS,
  SpinoffTwoFactorAuthCancelledError,
  isSpinoffTwoFactorAuthRequired,
  spinoffTwoFactorAuthAllowedMethods,
} from './spinoff-two-factor-auth.type';

/**
 * 보호 API가 `428 TWO_FACTOR_AUTH_REQUIRED`를 내리면 검증 다이얼로그를 열고, 인증을 마친 뒤 **같은 요청을 한 번만** 재전송한다.
 *
 * - 재전송이 또 428이면 다시 열지 않고 그대로 던진다(무한 루프 방지).
 * - 사용자가 취소하면 원래의 428 오류를 그대로 던진다 — 호출한 화면은 "인증이 필요합니다"를 알 수 있다.
 * - 인증을 시작하지 못한 오류(번호 없음 409 등)는 그 오류를 던진다. 안내는 앱이 `isSpinoffTwoFactorAuthPhoneUnverified`로 한다.
 * - 동시에 여러 요청이 428을 받아도 다이얼로그는 하나만 열린다(`SpinoffTwoFactorAuthService.ensureVerified`).
 * - `apiBaseUrl` 밖으로 가는 요청은 건드리지 않는다. 투팩터 인증 API(`/two-factor-auth/*`) 자신도 건드리지 않는다 — 그 호출이 428을 받으면
 *   (가드가 잘못 걸린 경우 등) 열려 있는 다이얼로그가 끝나기를 기다리는 자기 자신을 기다리는 교착이 생긴다.
 * - 428 본문에 `allowedMethods`가 없거나 비어 있으면(오래된 서버·본문을 고쳐 쓰는 프록시) 지원하는 기본 방식으로 시도한다. 이미 투팩터 인증이
 *   필요하다는 428임이 확인됐으므로 "지원하지 않는 방식"으로 막는 것보다 낫다.
 *
 * **등록 순서**: `spinoffAuthInterceptor`보다 **바깥(앞)** 에 둔다 — `withInterceptors([baseUrl, spinoffTwoFactorAuthInterceptor, spinoffAuthInterceptor])`.
 * 바깥에 두면 재전송이 인증 헤더 부착부터 다시 지나 최신 토큰이 실린다. 안쪽에 두면 이미 부착된 헤더로 재전송해 그 사이 갱신된 토큰이
 * 반영되지 않는다. 또 `apiBaseUrl`을 앞에 붙이는 인터셉터(앱별)는 이보다 앞에 있어야 URL 판정이 맞는다.
 */
export const spinoffTwoFactorAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(SPINOFF_AUTH_CONFIG);
  const service = inject(SpinoffTwoFactorAuthService);

  if (!req.url.startsWith(config.apiBaseUrl) || req.url.startsWith(`${config.apiBaseUrl}/two-factor-auth/`)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!isSpinoffTwoFactorAuthRequired(err)) return throwError(() => err);
      const allowed = spinoffTwoFactorAuthAllowedMethods(err);
      return from(service.ensureVerified(allowed.length > 0 ? allowed : SUPPORTED_SPINOFF_TWO_FACTOR_AUTH_METHODS)).pipe(
        // 프롬프트가 실패한 경우만 여기서 다룬다 — 취소는 원래의 428로 되돌린다(재전송 오류에는 적용되지 않는다).
        catchError((promptError: unknown) => throwError(() => (promptError instanceof SpinoffTwoFactorAuthCancelledError ? err : promptError))),
        // 한 번만 재전송한다. 재전송의 오류(또 428 포함)는 가공 없이 호출한 쪽으로 간다.
        switchMap(() => next(req)),
      );
    }),
  );
};
